import ActiveCaseFilterIds from "@/components/Forms/ActiveCaseFilterIds.tsx";
import EmployeeByType from "@/components/Forms/EmployeeByType.tsx";
import EmployeeDisplay from "@/components/Forms/EmployeeDisplay";
import { useBulkCaseReassignment } from "@/mutations/useBulkCaseReassignment.ts";
import type { CaseFilter } from "@/queries/useFetchCaseFiltersets.ts";
import {
    type CaseId,
    useFetchCaseIdsForActiveFilter,
} from "@/queries/useFetchCaseIdsForActiveFilter.ts";
import { useFetchTeams } from "@/queries/useFetchTeam.ts";
import { getErrorMessage } from "@/utils/api.ts";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Grid2,
    MenuItem,
    Modal,
    Stack,
    Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { AlertTriangle, Users } from "lucide-react";
import type {
    MRT_ColumnFiltersState,
    MRT_PaginationState,
    MRT_SortingState,
} from "material-react-table";
import { RhfTextField } from "mui-rhf-integration";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

const ModalContent = styled(Box)(({ theme }) => ({
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: 800,
    maxHeight: "90vh",
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[5],
    display: "flex",
    flexDirection: "column",
}));

const ContentArea = styled(Box)(({ theme }) => ({
    padding: theme.spacing(4),
    overflow: "auto",
    maxHeight: "calc(90vh - 140px)",
}));

const ModalFooter = styled(Box)(({ theme }) => ({
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2, 4),
    display: "flex",
    justifyContent: "flex-end",
    backgroundColor: theme.palette.background.paper,
}));

const sharedTextFieldSx = {
    "& .MuiInputBase-root": {
        height: 56,
    },
};

const sharedSlotProps = {
    select: {
        MenuProps: {
            sx: {
                "& .MuiMenuItem-root": {
                    minHeight: 40,
                },
            },
        },
    },
};

export type BulkReassignmentFields = {
    assignedTeam: string;
    assignedToAttorney: string;
    assignedToDisbursementSpecialist: string;
    assignedToIntakeSpecialist: string;
    assignedToParalegal: string;
    assignedToMedicalRecordsClerk: string;
};

interface BulkReassignmentModalProps {
    open: boolean;
    onClose: () => void;
    activeFilter: CaseFilter | null;
    columnFilters: MRT_ColumnFiltersState;
    globalFilter: string;
    pagination: MRT_PaginationState;
    sorting: MRT_SortingState;
}

const BulkReassignmentModal: React.FC<BulkReassignmentModalProps> = ({
    open,
    onClose,
    activeFilter,
    columnFilters,
    globalFilter,
    pagination,
    sorting,
}) => {
    const form = useForm<BulkReassignmentFields>();
    const [showSummary, setShowSummary] = useState(false);
    const [loading, setLoading] = useState(false);
    const doBulkCaseReassignment = useBulkCaseReassignment();
    const [selectedCases, setSelectedCases] = useState<CaseId[]>([]);
    const { data: teamsData } = useFetchTeams();
    const { data: dataCaseIds, isError } = useFetchCaseIdsForActiveFilter({
        selectedTab: "",
        taskDashboard: false,
        columnFilters,
        globalFilter,
        activeFilter,
        pagination,
        sorting,
    });

    const caseIds = dataCaseIds?.data ?? [];

    const tooManyCasesError = caseIds.length > 5000;

    const teamsWithBlank = [
        { value: "", label: "" },
        ...(teamsData?.map((team) => ({
            value: team.teamName || "",
            label: team.teamName || "",
        })) || []),
    ];

    const getAssignmentSummary = useCallback((): React.ReactNode[] => {
        const summary: React.ReactNode[] = [];

        if (!open) {
            return summary;
        }

        const formValues = form.getValues();

        if (formValues?.assignedTeam) {
            summary.push(
                <Stack key="team" sx={{ flexDirection: "row", gap: 1 }}>
                    <Typography variant={"body1"}>Team: </Typography>
                    <Typography variant={"body1"}>{formValues?.assignedTeam}</Typography>
                </Stack>,
            );
        }

        if (formValues?.assignedToAttorney) {
            summary.push(
                <Stack key="attorney" sx={{ flexDirection: "row", gap: 1 }}>
                    <Typography variant={"body1"}>Attorney: </Typography>
                    <EmployeeDisplay key="attorney" employeeUUID={formValues.assignedToAttorney} />
                </Stack>,
            );
        }

        if (formValues?.assignedToDisbursementSpecialist) {
            summary.push(
                <Stack key="disbursement-specialist" sx={{ flexDirection: "row", gap: 1 }}>
                    <Typography variant={"body1"}>Disbursement Specialist: </Typography>
                    <EmployeeDisplay
                        key="attorney"
                        employeeUUID={formValues.assignedToDisbursementSpecialist}
                    />
                </Stack>,
            );
        }

        if (formValues?.assignedToIntakeSpecialist) {
            summary.push(
                <Stack key="intake-specialist" sx={{ flexDirection: "row", gap: 1 }}>
                    <Typography variant={"body1"}>Intake Specialist: </Typography>
                    <EmployeeDisplay
                        key="attorney"
                        employeeUUID={formValues.assignedToIntakeSpecialist}
                    />
                </Stack>,
            );
        }

        if (formValues?.assignedToParalegal) {
            summary.push(
                <Stack key="paralegal" sx={{ flexDirection: "row", gap: 1 }}>
                    <Typography variant={"body1"}>Case Manager: </Typography>
                    <EmployeeDisplay key="attorney" employeeUUID={formValues.assignedToParalegal} />
                </Stack>,
            );
        }

        if (formValues?.assignedToMedicalRecordsClerk) {
            summary.push(
                <Stack key="medical-records-clerk" sx={{ flexDirection: "row", gap: 1 }}>
                    <Typography variant={"body1"}>Medical Records Clerk: </Typography>
                    <EmployeeDisplay
                        key="attorney"
                        employeeUUID={formValues.assignedToMedicalRecordsClerk}
                    />
                </Stack>,
            );
        }

        return summary;
    }, [open]);

    useEffect(() => {
        if (open) {
            setSelectedCases([]);
        }
    }, [open]);

    const handleBulkReassignment = () => {
        doBulkCaseReassignment.mutate(
            {
                caseIds: selectedCases.map((caseId) => caseId.idUUID),
                assignments: form.getValues(),
            },
            {
                onSuccess: () => {
                    setLoading(false);
                    handleClose();
                },
                onError: (error) => {
                    console.info("error", getErrorMessage(error));
                    setLoading(false);
                },
            },
        );
    };

    const handleShowSummary = () => {
        setShowSummary(true);
    };

    const handleClose = () => {
        setShowSummary(false);
        setSelectedCases([]);
        onClose();
    };

    const getSingleValue = (value: string | string[] | null | undefined): string => {
        if (Array.isArray(value)) {
            return value[0] || "";
        }

        return value || "";
    };

    useEffect(() => {
        if (open) {
            form.reset({
                assignedTeam: getSingleValue(activeFilter?.assignedTeam),
                assignedToAttorney: getSingleValue(activeFilter?.assignedToAttorney),
                assignedToDisbursementSpecialist: getSingleValue(
                    activeFilter?.assignedToDisbursementSpecialist,
                ),
                assignedToIntakeSpecialist: getSingleValue(
                    activeFilter?.assignedToIntakeSpecialist,
                ),
                assignedToParalegal: getSingleValue(activeFilter?.assignedToParalegal),
                assignedToMedicalRecordsClerk: getSingleValue(
                    activeFilter?.assignedToMedicalRecordsClerk,
                ),
            });
        }
    }, [open, activeFilter]);

    return (
        <Modal open={open} onClose={handleClose}>
            <ModalContent>
                <ContentArea>
                    <FormProvider {...form}>
                        <Stack
                            sx={{
                                flexDirection: "row",
                                alignItems: "center",
                                mb: 2,
                            }}
                        >
                            <Users />

                            <Typography variant="h5" component="h2" sx={{ ml: 2 }}>
                                {showSummary ? "Confirm Bulk Assignment" : "Bulk Assignment"}
                            </Typography>
                        </Stack>

                        {tooManyCasesError && (
                            <Alert severity="error" sx={{ mb: 3 }}>
                                Bulk assignment is limited to 5000 cases.
                            </Alert>
                        )}

                        {!tooManyCasesError && (
                            <>
                                {isError && (
                                    <Alert severity="error" sx={{ mb: 3 }}>
                                        Something went wrong while fetching data. Please try again
                                        or contact support.
                                    </Alert>
                                )}

                                {!showSummary && (
                                    <Box>
                                        <Box
                                            sx={{
                                                gap: 2,
                                                mb: 2,
                                            }}
                                        >
                                            <Chip
                                                label={`${caseIds.length} Cases Selected`}
                                                icon={<Users size={16} />}
                                                sx={{
                                                    fontWeight: 500,
                                                }}
                                            />
                                        </Box>

                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ mb: 3 }}
                                        >
                                            Select the fields you want to update for the filtered
                                            cases. Only the fields you select will be updated.
                                        </Typography>

                                        <Grid2 container spacing={2}>
                                            <Grid2 size={4}>
                                                <RhfTextField
                                                    select
                                                    slotProps={sharedSlotProps}
                                                    name="assignedTeam"
                                                    control={form.control}
                                                    label="Team"
                                                    fullWidth
                                                    sx={sharedTextFieldSx}
                                                >
                                                    {teamsWithBlank.map(
                                                        (option: {
                                                            value: string;
                                                            label: string;
                                                        }) => (
                                                            <MenuItem
                                                                key={option.label}
                                                                value={String(option.value)}
                                                            >
                                                                {option.label}
                                                            </MenuItem>
                                                        ),
                                                    )}
                                                </RhfTextField>
                                            </Grid2>
                                            <Grid2 size={4}>
                                                <EmployeeByType
                                                    label={"Attorney"}
                                                    name={"assignedToAttorney"}
                                                    type={"Attorney"}
                                                    sx={sharedTextFieldSx}
                                                />
                                            </Grid2>
                                            <Grid2 size={4}>
                                                <EmployeeByType
                                                    label={"Disbursement Specialist"}
                                                    name={"assignedToDisbursementSpecialist"}
                                                    type={"DisbursementSpecialist"}
                                                    sx={sharedTextFieldSx}
                                                />
                                            </Grid2>
                                        </Grid2>

                                        <Grid2 container spacing={2} sx={{ mt: 3 }}>
                                            <Grid2 size={4}>
                                                <EmployeeByType
                                                    label={"Intake Specialist"}
                                                    name={"assignedToIntakeSpecialist"}
                                                    type={"IntakeSpecialist"}
                                                    sx={sharedTextFieldSx}
                                                />
                                            </Grid2>
                                            <Grid2 size={4}>
                                                <EmployeeByType
                                                    label={"Case Manager"}
                                                    name={"assignedToParalegal"}
                                                    type={"CaseManager"}
                                                    sx={sharedTextFieldSx}
                                                />
                                            </Grid2>
                                            <Grid2 size={4}>
                                                <EmployeeByType
                                                    label={"Medical Records Clerk"}
                                                    name={"assignedToMedicalRecordsClerk"}
                                                    type={"MedicalRecordsClerk"}
                                                    sx={sharedTextFieldSx}
                                                />
                                            </Grid2>
                                        </Grid2>
                                    </Box>
                                )}
                                {showSummary && (
                                    <Box>
                                        <Alert
                                            severity="warning"
                                            icon={<AlertTriangle size={20} />}
                                            sx={{ mb: 3 }}
                                        >
                                            <Typography variant="subtitle2" gutterBottom>
                                                Please confirm the following bulk assignment:
                                            </Typography>
                                        </Alert>

                                        <Typography variant="h6" gutterBottom>
                                            Assignment Summary
                                        </Typography>

                                        <Box sx={{ mb: 3 }}>
                                            {getAssignmentSummary().map((assignment, index) => (
                                                <Typography
                                                    key={index}
                                                    variant="body2"
                                                    sx={{ mb: 0.5 }}
                                                >
                                                    {assignment}
                                                </Typography>
                                            ))}
                                        </Box>

                                        <Typography variant="h6" gutterBottom>
                                            Cases to be Updated ({selectedCases.length} of{" "}
                                            {caseIds.length})
                                        </Typography>

                                        <Box sx={{ mt: 2 }}>
                                            {activeFilter && (
                                                <ActiveCaseFilterIds
                                                    caseIds={caseIds}
                                                    selectedCases={selectedCases}
                                                    setSelectedCases={setSelectedCases}
                                                />
                                            )}
                                        </Box>
                                    </Box>
                                )}
                            </>
                        )}
                    </FormProvider>
                </ContentArea>

                {!(tooManyCasesError || showSummary) && (
                    <ModalFooter>
                        <Button onClick={handleClose} color="inherit" sx={{ mr: 2 }}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleShowSummary}
                            variant="contained"
                            color="primary"
                            disabled={Object.values(form.watch()).every((value) => !value)}
                        >
                            Next
                        </Button>
                    </ModalFooter>
                )}

                {!tooManyCasesError && showSummary && (
                    <ModalFooter>
                        <Button onClick={handleClose} color="inherit" sx={{ mr: 2 }}>
                            Cancel
                        </Button>
                        <Button
                            onClick={() => setShowSummary(false)}
                            variant="contained"
                            color="primary"
                            sx={{ mr: 2 }}
                        >
                            Back
                        </Button>
                        {!loading && (
                            <Button
                                onClick={handleBulkReassignment}
                                variant="contained"
                                color="primary"
                                disabled={selectedCases.length <= 0}
                            >
                                Confirm Assignment
                            </Button>
                        )}
                        {loading && <CircularProgress />}
                    </ModalFooter>
                )}

                {tooManyCasesError && (
                    <ModalFooter>
                        <Button onClick={handleClose} color="inherit">
                            Cancel
                        </Button>
                    </ModalFooter>
                )}
            </ModalContent>
        </Modal>
    );
};

export default BulkReassignmentModal;
